import { Box, ButtonGroup, Card, Container, Divider, FormLabel, SkeletonText, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import iconArrowLeftRight from 'assets/icons/icon-arrow-left-right.svg';
import EmptyList from 'components/ui/EmptyList';
import HeaderBar from 'components/ui/HeaderBar';
import { useMessages } from 'hooks/useMessages';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ButtonNewNote from '../../components/ButtonNewNote';
import { useDuplicateNote } from '../../hooks/useDuplicateNote';
import {
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import CustomRangeDatepicker from 'components/ui/Datepicker/CustomRangeDatepicker';
import DateTime from 'components/ui/DateTime';
import FilterGroup from 'components/ui/FilterGroup';
import IconButtonEdit from 'components/ui/IconButtonEdit';
import Pagination from 'components/ui/Pagination';
import SearchInput from 'components/ui/SearchInput';
import ServerDynamicTableProvider, {
  useServerDynamicTableContext,
} from 'contexts/server-table/ServerDynamicTableContext';
import ButtonRemoveResource from 'components/ButtonRemoveResource';
import useServerTableUrlState from 'hooks/server-table/useServerTableUrlState';
import ButtonDuplicateNote from '../../components/ButtonDuplicateNote';

type NotesFilterKey = 'dateFrom' | 'dateTo';

const notesFilterDefaults: Record<NotesFilterKey, string> = {
  dateFrom: '',
  dateTo: '',
};

export default function PageNotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    pagination,
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  } = useServerTableUrlState<NotesFilterKey>({
    filterDefaults: notesFilterDefaults,
  });

  const selectedDateRange = React.useMemo(() => {
    const dates: Date[] = [];
    const fromDate = parseIsoDate(filters.dateFrom);
    const toDate = parseIsoDate(filters.dateTo);

    if (fromDate) {
      dates.push(fromDate);
    }
    if (toDate) {
      dates.push(toDate);
    }
    return dates;
  }, [filters.dateFrom, filters.dateTo]);

  const resourceParams = React.useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      q: globalFilter || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    }),
    [
      filters.dateFrom,
      filters.dateTo,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
    ],
  );

  const columns = React.useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        header: 'Data do evento',
        accessorKey: 'date',
        cell: props => <DateTime date={props.cell.getValue()} hideTime />,
      },
      {
        header: 'Modelo de disparo',
        accessorKey: 'contentGroup.name',
      },
      {
        header: 'Categoria',
        accessorFn: (row) => `${row.category.name} - ${row.subcategory.name}`,
      },
      {
        header: 'Título',
        accessorKey: 'title'
      },
      {
        header: 'Criado por',
        accessorKey: 'createdBy.name'
      },
    ],
    []
  )

  const { onError } = useMessages();

  const { onDuplicateNote } = useDuplicateNote({
    onSuccess: (noteId) => {
      navigate({
        pathname: `/notas-de-agenda/editar/${noteId}`,
        search: location.search,
      });
    },
    onError: (error) => onError(error?.response?.data?.message)
  });

  return (
    <>
      <ServerDynamicTableProvider
        columns={columns}
        pagination={pagination}
        globalFilter={globalFilter}
        onPaginationChange={setPagination}
        onGlobalFilterChange={setGlobalFilter}
        resource={{
          resourceName: 'notes',
          params: resourceParams,
        }}
      >
        <HeaderBar
          title="Notas de Agenda"
          description="Visualize e gerencie as notas da agenda"
        >
          <ButtonNewNote />
        </HeaderBar>

        <Divider />

        <Container flexDirection="column">
          <Filters
            globalFilter={globalFilter}
            onGlobalFilterChange={setGlobalFilter}
            dateRange={selectedDateRange}
            onDateRangeChange={(dates) => {
              const [startDate, endDate] = dates;
              setFilter('dateFrom', startDate ? startOfDayIso(startDate) : '');
              setFilter('dateTo', endDate ? endOfDayIso(endDate) : '');
            }}
          />
          <List onDuplicateNote={onDuplicateNote} />
        </Container>
      </ServerDynamicTableProvider>
    </>
  )
}

function Filters({
  globalFilter,
  onGlobalFilterChange,
  dateRange,
  onDateRangeChange,
}: {
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  dateRange: Date[];
  onDateRangeChange: (dates: Date[]) => void;
}) {
  return (
    <FilterGroup justifyContent="space-between" zIndex={1} pos="relative">
      <Box flex={1} minW="180px">
        <FormLabel>Buscar</FormLabel>
        <SearchInput
          placeholder="Buscar por modelo de disparo, categoria, título ou autor"
          value={globalFilter}
          onChange={value => onGlobalFilterChange(String(value))}
        />
      </Box>
      <Box>
        <FormLabel>Filtrar por período</FormLabel>
        <CustomRangeDatepicker
          dates={dateRange}
          onDateChange={onDateRangeChange}
        />
      </Box>
    </FilterGroup>
  );
}

function List({ onDuplicateNote }: { onDuplicateNote: (noteId: string) => void }) {
  const location = useLocation();
  const { table, queryResult } = useServerDynamicTableContext();
  const total = queryResult?.data?.meta?.total || 0;
  const isLoading = queryResult?.isLoading || false;
  const hasData = total > 0;
  const hasResult = table.getRowModel().rows.length > 0;

  return (
    <SkeletonText isLoaded={!isLoading}>
      {hasResult && (
        <>
          <Table mt="25px">
            <Thead>
              <Tr>
                <Th>Data do evento</Th>
                <Th>Modelo de disparo</Th>
                <Th>Categoria</Th>
                <Th>Título</Th>
                <Th>Criado por</Th>
                <Th w="125px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const rowData = row.original as Record<string, unknown>;

                return (
                  <Tr key={`row-${row.id}`}>
                    {row.getVisibleCells().map(cell => (
                      <Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                    <Td textAlign="right">
                      <ButtonGroup>
                        <ButtonDuplicateNote onClick={() => onDuplicateNote(String(rowData.id))} />
                        <IconButtonEdit
                          to={{
                            pathname: `/notas-de-agenda/editar/${String(rowData.id)}`,
                            search: location.search,
                          }}
                        />
                        <ButtonRemoveResource
                          id={String(rowData.id)}
                          pathName="notes"
                          message="Você tem certeza disso? Essa nota e seus tópicos também serão removidos de todos os Disparos de Agenda que os utilizam."
                          queryKey="notes"
                        />
                      </ButtonGroup>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <Pagination table={table} />
        </>
      )}

      {!hasData && (
        <Card w="full" p="40px" mt="53px">
          <EmptyList
            icon={iconArrowLeftRight}
            title={'Esse disparo de Agenda não possui nenhuma nota cadastrada'}
            description={'Use o botão acima para cadastrar'}
            isTable
          />
        </Card>
      )}

      {hasData && !hasResult && (
        <EmptyList
          mt="25px"
          icon={iconArrowLeftRight}
          title="Nenhum resultado encontrado"
          description=""
          isTable
        />
      )}
    </SkeletonText>
  );
}

const parseIsoDate = (date?: string) => {
  if (!date) {
    return null;
  }
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return parsedDate;
};

const startOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate.toISOString();
};

const endOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate.toISOString();
};